import React from "react";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiEmailOutline, mdiMessageTextOutline } from "@mdi/js";
import CardShell from "./CardShell";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import { REPORT_BUG_HELP_LINK } from "app/constants/global-constants";
import { emailRowMeta } from "app/containers/Jobs/utils/emailRowMeta";

// Per-step action: "Send Now" for a ready step, "Copy Content" when a step
// failed or was canceled (so the inspector can send manually). Deferred to the
// Email Automation feature for real wiring.
const stepAction = (status) => {
  if (status === "ready_to_send")
    return { label: "Send Now", variant: "primary", key: "Send email" };
  if (status === "failed" || status === "canceled")
    return {
      label: "Copy Content",
      variant: "outline-primary",
      key: "Copy email content",
    };
  return null;
};

const EmptyPrompt = ({ heading, actionLabel, onAction }) => (
  <div className="jd-email-empty">
    <div className="jd-muted-heading">{heading}</div>
    <Button variant="primary" size="sm" onClick={onAction}>
      {actionLabel}
    </Button>
  </div>
);

const EmailAutomationCard = ({
  job,
  onDeferred,
  isFreeTier,
  mailboxState,
  onUpgrade,
  onSetUpEmail,
}) => {
  const steps = (job.emails || []).filter((step) => step.status);

  const emptyBody = () => {
    if (isFreeTier)
      return (
        <EmptyPrompt
          heading="Send automated emails at every step"
          actionLabel="Upgrade to Solo"
          onAction={onUpgrade}
        />
      );
    if (mailboxState === "loading") return null;
    if (mailboxState === "missing")
      return (
        <EmptyPrompt
          heading="Set up automated emails for every step"
          actionLabel="Set Up Email Automation"
          onAction={onSetUpEmail}
        />
      );
    return <div className="jd-subtle">No emails scheduled for this job.</div>;
  };

  return (
    <CardShell
      icon={<Icon path={mdiEmailOutline} size={0.7} className="jd-card-icon" />}
      title="Email Automation"
    >
      {steps.length === 0 ? (
        emptyBody()
      ) : (
        <ul className="jd-email-list">
          {steps.map((step) => {
            const action = stepAction(step.status);
            const isFailed = step.status === "failed";
            const meta = emailRowMeta(step);
            return (
              <li key={step.id} className="jd-email-row">
                <span className="jd-email-name">{step.name}</span>
                <JobStatusBadge domain="email" value={step.status} />
                {/* Each meta owns a grid row on mobile, so an empty one costs
                    a visible gap — omit it instead. */}
                {meta && (
                  <span
                    className={
                      meta.tone === "warn"
                        ? "jd-email-meta jd-email-meta--warn"
                        : "jd-email-meta"
                    }
                  >
                    {meta.text}
                  </span>
                )}
                {(action || isFailed) && (
                  <span className="jd-email-action">
                    {isFailed && (
                      <Button
                        variant="link"
                        size="sm"
                        className="jd-email-report-issue"
                        aria-label="Report issue"
                        title="Report an issue"
                        onClick={() =>
                          window.open(
                            REPORT_BUG_HELP_LINK,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <Icon path={mdiMessageTextOutline} size={0.7} />
                      </Button>
                    )}
                    {action && (
                      <Button
                        variant={action.variant}
                        size="sm"
                        onClick={() => onDeferred(action.key)}
                      >
                        {action.label}
                      </Button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </CardShell>
  );
};

export default EmailAutomationCard;
